import React, { useMemo, useState } from "react";
import { Alert, Button, Col, Row } from "react-bootstrap";
import { createColumnHelper } from "@tanstack/react-table";
import { GenericTable } from "components/GenericTable";
import EditableCell from "containers/common/EditableCell";
import "containers/common/List.css";
import "containers/common/Table.css";

function NaberList({
  data,
  isAuthenticated = true,
  controlType,
  saveRecord,
  deleteRecord,
  updateTableState,
  nabersRatingTypeOptions,
  nabersRatingValues,
  filter,
  validateRelatedRecord,
  user,
  propertyid,
}) {
  const dataSet = "nabers";
  const tableData = data || [];
  const columnHelper = createColumnHelper();
  const [showAlert, setShowAlert] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const ratingTypeOptions = useMemo(
    () => (Array.isArray(nabersRatingTypeOptions) ? nabersRatingTypeOptions : []),
    [nabersRatingTypeOptions]
  );
  const ratingValueOptions = useMemo(
    () => (Array.isArray(nabersRatingValues) ? nabersRatingValues : []),
    [nabersRatingValues]
  );

  const tableMeta = {
    updateData: (rowIndex, columnId, value) => {
      updateTableState(dataSet, rowIndex, columnId, value);
    },
    handleSave: async ({ row, column, value }) => {
      const record = {
        ...(tableData[row.index] || {}),
        ...row.original,
        ...(column?.id ? { [column.id]: value } : {}),
        last_edited_by: user,
      };

      if (column?.id === "rating_area") {
        const parsed = Number(record.rating_area);
        record.rating_area = Number.isFinite(parsed) ? parsed : null;
      }

      const validation = validateRelatedRecord?.(record, dataSet) || [true, null];
      if (validation[0] !== true) {
        setErrorMessage(validation[1] || "Validation failed");
        setShowAlert(true);
        return { validationFailed: true };
      }

      setShowAlert(false);
      setErrorMessage("");

      try {
        await saveRecord(dataSet, record);
      } catch (error) {
        console.error("Nabers save failed:", error);
        setErrorMessage(error?.message || "Failed to save NABERS record");
        setShowAlert(true);
        return { saveFailed: true };
      }

      return true;
    },
    handleDelete: async ({ row }) => {
      await deleteRecord(dataSet, row.original);

      const deletedId = row.original.nabers_id;
      const deletedClientId = row.original._client_id;
      const nextRows = tableData.filter((item) => {
        const matchesSavedId =
          deletedId !== null &&
          deletedId !== undefined &&
          String(item.nabers_id) === String(deletedId);
        const matchesClientId =
          deletedClientId !== null &&
          deletedClientId !== undefined &&
          item._client_id === deletedClientId;
        return !(matchesSavedId || matchesClientId);
      });

      updateTableState(nextRows, dataSet);
    },
  };

  const columns = [
    columnHelper.accessor("nabers_id", {
      id: "nabers_id",
      header: "ID",
      size: 60,
    }),
    columnHelper.accessor("building_name", {
      id: "building_name",
      header: "Building",
      cell: EditableCell,
      size: 220,
    }),
    columnHelper.accessor("beec_nabers_id", {
      id: "beec_nabers_id",
      header: "BEEC Nabers ID",
      cell: EditableCell,
      size: 140,
    }),
    columnHelper.accessor("rating_expiry_date", {
      id: "rating_expiry_date",
      header: "Rating Expiry Date",
      cell: EditableCell,
      size: 135,
      meta: { cellStyle: { textAlign: "center" } },
    }),
    columnHelper.accessor("rating_type", {
      id: "rating_type",
      header: "Rating Type",
      cell: EditableCell,
      size: 160,
      meta: { options: ratingTypeOptions },
    }),
    columnHelper.accessor("rating_area", {
      id: "rating_area",
      header: "Rating Area",
      cell: EditableCell,
      size: 110,
      meta: {
        inputType: "number",
        numericOnly: true,
        min: 0,
        step: 1,
        cellStyle: { textAlign: "right" },
      },
    }),
    columnHelper.accessor("energy_rating", {
      id: "energy_rating",
      header: "Energy Rating",
      cell: EditableCell,
      size: 120,
      meta: { options: ratingValueOptions },
    }),
    columnHelper.accessor("energy_rating_no_gp", {
      id: "energy_rating_no_gp",
      header: "Energy Rating - No Green Power",
      cell: EditableCell,
      size: 190,
      meta: { options: ratingValueOptions },
    }),
    columnHelper.accessor("water_rating", {
      id: "water_rating",
      header: "Water Rating",
      cell: EditableCell,
      size: 120,
      meta: { options: ratingValueOptions },
    }),
    columnHelper.accessor("waste_rating", {
      id: "waste_rating",
      header: "Waste Rating",
      cell: EditableCell,
      size: 120,
      meta: { options: ratingValueOptions },
    }),
    columnHelper.accessor("indoor_env_rating", {
      id: "indoor_env_rating",
      header: "Indoor Env Rating",
      cell: EditableCell,
      size: 130,
      meta: { options: ratingValueOptions },
    }),
    columnHelper.accessor("date_last_checked", {
      id: "date_last_checked",
      header: "Date Last Checked",
      cell: EditableCell,
      size: 135,
      meta: { cellStyle: { textAlign: "center" } },
    }),
    columnHelper.display({
      id: "delete_nabers",
      header: "",
      cell: ({ row }) => (
        <Button
          type="button"
          size="sm"
          variant="danger"
          onClick={async (event) => {
            event.preventDefault();
            const confirmed = window.confirm("Are you sure you want to delete this record?");
            if (confirmed) {
              await tableMeta.handleDelete({ row });
            }
          }}
        >
          Delete
        </Button>
      ),
      size: 80,
      enableSorting: false,
    }),
  ];

  function addRecord() {
    const nextRows = [
      {
        _client_id: `nabers-${Date.now()}-${tableData.length}`,
        property_id: filter?.propertyid || propertyid || null,
        created_by: user,
        last_edited_by: user,
        new_record: true,
        modified: true,
        nabers_id: null,
        building_name: null,
        beec_nabers_id: null,
        rating_expiry_date: new Date(),
        rating_type: null,
        rating_area: null,
        energy_rating: null,
        energy_rating_no_gp: null,
        water_rating: null,
        waste_rating: null,
        indoor_env_rating: null,
        date_last_checked: new Date(),
      },
      ...tableData,
    ];

    updateTableState(nextRows, dataSet);
  }

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="NabersSection">
      {showAlert && <Alert variant="danger">{errorMessage}</Alert>}

      {controlType === "table" && (
        <Row className="show-Container">
          <Col className="text-right">
            <Button variant="secondary" size="sm" onClick={addRecord}>
              Add
            </Button>
          </Col>
        </Row>
      )}

      <Row className="show-Container">
        <Col sm={12} md={12} lg={12}>
          <GenericTable
            columns={columns}
            data={tableData}
            pageSize={10}
            enablePagination={true}
            hasHeaderSorting={true}
            meta={tableMeta}
          />
        </Col>
      </Row>
    </div>
  );
}

export default NaberList;
